import React, { useEffect, useRef, useState } from 'react';
import {
    Modal,
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    ScrollView,
    Image,
    Platform,
    Animated,
    BackHandler,
} from 'react-native';

import { MainNavProps } from '../navigation/main/MainParamList';
import { AppUpgradeInfo } from '../../net/response';
import { Str_FindNewVersion, Str_UpdateLater, Str_UpdateNow } from '../../language/string_res';
import { showToast } from '../../components/Toast';
import { isAndroid, isIos } from '../../AppEvent';

type UpgradeGuideProps = {
    visible?: boolean;
    isShowProgress?: boolean;
    progress?: number;
    onClose?: () => void;
    onUpdate?: () => void;
} & AppUpgradeInfo;

/**
 * 简易圆形进度（基于底部填充模拟，跨平台无需额外依赖）
 */
const CircleProgress = ({ size = 72, progress = 0, strokeColor = '#FF8C42', bgColor = '#F2F2F2' }: {
    size?: number;
    progress?: number;
    strokeColor?: string;
    bgColor?: string;
}) => {
    const percent = Math.max(0, Math.min(100, Math.round(progress)));
    const radius = size / 2;
    return (
        <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
            <View style={[styles.progressCircle, { width: size, height: size, borderRadius: radius, backgroundColor: bgColor }]}>
                <View style={[styles.progressFill, {
                    height: `${percent}%`,
                    backgroundColor: strokeColor,
                    bottom: 0,
                }]} />
            </View>
            <View style={[StyleSheet.absoluteFillObject, { alignItems: 'center', justifyContent: 'center' }]}>
                <Text style={{ color: '#fff', fontWeight: '700' }}>{percent}%</Text>
            </View>
        </View>
    );
};

/**
 * 纯展示组件 — 不处理 navigation
 */
export const UpgradeGuide = (props: UpgradeGuideProps) => {
    const { version, description, force = false, isShowProgress = false, progress = 0, onClose, onUpdate } = props;

    const renderChangelog = () => {
        if (!description) return null;
        if (Array.isArray(description)) {
            return description.map((line, idx) => (
                <View key={idx} style={styles.changeLine}>
                    <Text style={styles.bullet}>·</Text>
                    <Text style={styles.changeText}>{line}</Text>
                </View>
            ));
        }
        return <Text style={styles.changeText}>{description}</Text>;
    };

    return (
        <View style={styles.innerContainer}>
            <View style={styles.header}>
                {/* <Image style={styles.icon} /> */}
                <Text style={styles.title}>{Str_FindNewVersion} V{version}</Text>
            </View>

            <ScrollView style={styles.content} contentContainerStyle={{ paddingVertical: 6 }}>
                {renderChangelog()}
            </ScrollView>

            {isShowProgress && (
                <View style={{ marginVertical: 8, alignItems: 'center' }}>
                    <CircleProgress size={72} progress={progress} />
                </View>
            )}

            {!isShowProgress && (<View style={styles.footer}>
                {!force && (
                    <TouchableOpacity
                        style={[styles.btn, styles.btnSecondary]}
                        activeOpacity={0.8}
                        onPress={onClose}
                    >
                        <Text style={styles.btnSecondaryText}>{Str_UpdateLater}</Text>
                    </TouchableOpacity>
                )}

                <TouchableOpacity
                    style={[styles.btn, styles.btnPrimary, force && { flex: 1 }]}
                    activeOpacity={0.8}
                    onPress={onUpdate}
                >
                    <Text style={styles.btnPrimaryText}>{Str_UpdateNow}</Text>
                </TouchableOpacity>
            </View>)}
        </View>
    );
};

/**
 * Screen wrapper — 通过 navigation 路由展示，并带弹出动画
 * route.params 应包含: { version, changelog?, force?, packageUrl? }
 */
export const UpgradeGuideScreen = ({ navigation, route }: MainNavProps<'UpgradeGuideScreen'>) => {
    const { force = false, version, description } = route?.params as AppUpgradeInfo;

    const backdropAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(0.9)).current;

    // 下载/进度状态
    const [isDownloading, setIsDownloading] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        // 入场动画...

        // 设置屏幕选项，禁止侧滑返回
        navigation.setOptions({
            gestureEnabled: !force, // 强制更新时禁用，非强制时允许
        });

        // 拦截物理后退键
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            // 强制更新时完全阻止返回
            if (force) {
                return true; // 阻止返回
            }
            // 非强制时允许返回
            return false;
        });

        return () => {
            backHandler.remove();
            // 可选：恢复侧滑手势
            navigation.setOptions({
                gestureEnabled: true,
            });
        };
    }, [navigation, force]);

    useEffect(() => {
        // 入场动画
        Animated.parallel([
            Animated.timing(backdropAnim, { toValue: 1, duration: 200, useNativeDriver: false }),
            Animated.spring(scaleAnim, { toValue: 1, friction: 7, useNativeDriver: false }),
        ]).start();

        // 如果是强制更新，拦截物理后退键
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            return !!force; // 强制时阻止返回（返回 true 阻止）
        });

        return () => backHandler.remove();
    }, []);

    const closeWithAnim = (cb?: () => void) => {
        // 出场动画后关闭 route
        Animated.parallel([
            Animated.timing(backdropAnim, { toValue: 0, duration: 180, useNativeDriver: false }),
            Animated.timing(scaleAnim, { toValue: 0.95, duration: 160, useNativeDriver: false }),
        ]).start(() => {
            cb?.();
            navigation.goBack();
        });
    };

    const handleClose = () => {
        if (force) return; // 强制更新不允许关闭
        closeWithAnim();
    };

    const handleUpdate = () => {
        if (force) {
            // 强制更新时不关闭当前页面
            upgradeAppNow();
            return;
        }

        if (isAndroid) {
            upgradeAppNow()
        } else {
            closeWithAnim(upgradeAppNow);
        }
    };

    const onDownLoadApkProgress = (percent: number, written: number, total: number) => {
        setProgress(percent);
    }

    const upgradeAppNow = async () => {
    }

    return (
        <Modal visible transparent animationType="fade" onRequestClose={() => { if (!force) navigation.goBack(); }}>
            <Animated.View style={[styles.backdrop, { opacity: backdropAnim }]}>
                <Animated.View
                    style={{
                        transform: [{ scale: scaleAnim }],
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        alignItems: 'center',
                    }}
                >
                    <View style={styles.container}>
                        <UpgradeGuide
                            visible={true}
                            version={version}
                            description={description}
                            force={force}
                            isShowProgress={isDownloading}
                            progress={progress}
                            onClose={handleClose}
                            onUpdate={handleUpdate}
                        />
                    </View>
                </Animated.View>
            </Animated.View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.45)',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 20,
    },
    backdropTouchable: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    container: {
        width: '100%',
        maxWidth: 320,
        // container 内部由 UpgradeGuide 渲染样式
        alignItems: 'center',
    },
    innerContainer: {
        backgroundColor: '#fff',
        borderRadius: 14,
        overflow: 'hidden',
        paddingHorizontal: 18,
        paddingTop: 18,
        paddingBottom: 14,
        alignItems: 'center',
        width: '100%',
    },
    header: {
        alignItems: 'center',
        marginBottom: 8,
    },
    icon: {
        resizeMode: 'cover',
    },
    title: {
        fontSize: 16,
        color: '#1a1a1a',
        fontWeight: '700',
    },
    content: {
        alignSelf: 'stretch',
        maxHeight: 220,
        marginTop: 8,
        marginBottom: 12,
    },
    changeLine: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingVertical: 6,
    },
    bullet: {
        width: 18,
        color: '#888',
        fontSize: 14,
        lineHeight: 20,
    },
    changeText: {
        flex: 1,
        color: '#333',
        fontSize: 14,
        lineHeight: 20,
    },
    footer: {
        flexDirection: 'row',
        alignSelf: 'center',
        justifyContent: 'space-between',
        paddingTop: 6,
    },
    btn: {
        flex:1,
        minWidth: 92,
        paddingVertical: Platform.OS === 'ios' ? 10 : 8,
        borderRadius: 8,
        alignItems: 'center',
        marginHorizontal: 6,
        minHeight: 45,
        justifyContent: 'center',
    },
    btnPrimary: {
        backgroundColor: '#FF8C42',
    },
    btnPrimaryText: {
        color: '#fff',
        fontWeight: '700',
        fontSize: 15,
    },
    btnSecondary: {
        backgroundColor: '#F2F2F2',
    },
    btnSecondaryText: {
        color: '#444',
        fontSize: 15,
    },

    /* CircleProgress styles */
    progressCircle: {
        overflow: 'hidden',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    progressFill: {
        position: 'absolute',
        left: 0,
        right: 0,
    },
});